// components/patient/Messages.jsx
import React, { useState, useEffect } from 'react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Icon } from '../ui/Icon';
import { Input } from '../ui/Input';
import { useBrand } from '../../contexts/BrandContext';
import { useToast } from '../../contexts/ToastContext';
import { DB } from '../../utils/db';

export function Messages({ user, onNavigate }) {
  const { brand } = useBrand();
  const toast = useToast();
  const [threads, setThreads] = useState([]);
  const [selectedThread, setSelectedThread] = useState(null);
  const [newMessage, setNewMessage] = useState('');
  
  useEffect(() => {
    loadThreads();
  }, [user.id]);
  
  const loadThreads = async () => {
    const allThreads = await DB.get('d2p_message_threads') || [];
    const userThreads = allThreads.filter(t => t.userId === user.id);
    setThreads(userThreads);
    
    // Select first thread if available
    if (userThreads.length > 0 && !selectedThread) {
      setSelectedThread(userThreads[0]);
    }
  };
  
  const handleSendMessage = async () => {
    if (!newMessage.trim()) return;
    
    const message = {
      id: `msg_${Date.now()}`,
      sender: 'patient',
      content: newMessage.trim(),
      timestamp: new Date().toISOString()
    };
    
    if (selectedThread) {
      // Add to existing thread
      const updatedThreads = threads.map(t => {
        if (t.id === selectedThread.id) {
          return {
            ...t,
            messages: [...t.messages, message],
            lastMessageAt: message.timestamp
          };
        }
        return t;
      });
      
      await DB.set('d2p_message_threads', updatedThreads);
      setThreads(updatedThreads);
      setSelectedThread(updatedThreads.find(t => t.id === selectedThread.id));
    } else {
      // Create new thread
      const newThread = {
        id: `thread_${Date.now()}`,
        userId: user.id,
        subject: 'General Inquiry',
        status: 'open',
        messages: [message],
        createdAt: new Date().toISOString(),
        lastMessageAt: message.timestamp
      };
      
      const updatedThreads = [...threads, newThread];
      await DB.set('d2p_message_threads', updatedThreads);
      setThreads(updatedThreads);
      setSelectedThread(newThread);
    }
    
    setNewMessage('');
    toast.addToast('Message sent');
  };
  
  return (
    <div style={{
      background: '#F9FAFB',
      minHeight: '100vh',
      padding: '40px 24px',
      fontFamily: 'Libre Franklin, sans-serif'
    }}>
      <div style={{ maxWidth: 1200, margin: '0 auto' }}>
        <Button
          variant="ghost"
          icon="arrowLeft"
          onClick={() => onNavigate('dashboard')}
          style={{ marginBottom: 24 }}
        >
          Back to Dashboard
        </Button>
        
        <h1 style={{ fontSize: 32, fontWeight: 700, marginBottom: 8, color: brand.c.navy }}>
          Messages
        </h1>
        <p style={{ color: '#6B7280', marginBottom: 32 }}>
          Contact our pharmacy team with questions
        </p>
        
        <div style={{
          display: 'grid',
          gridTemplateColumns: threads.length > 0 ? '300px 1fr' : '1fr',
          gap: 24,
          alignItems: 'start'
        }}>
          {/* Thread List */}
          {threads.length > 0 && (
            <Card>
              <h2 style={{ fontSize: 18, fontWeight: 700, marginBottom: 16, color: brand.c.navy }}>
                Conversations
              </h2>
              <div style={{ display: 'grid', gap: 8 }}>
                {threads.map(thread => (
                  <button
                    key={thread.id}
                    onClick={() => setSelectedThread(thread)}
                    style={{
                      padding: 12,
                      border: 'none',
                      borderRadius: 8,
                      background: selectedThread?.id === thread.id ? `${brand.c.accent}15` : '#F9FAFB',
                      cursor: 'pointer',
                      textAlign: 'left',
                      transition: 'all 0.2s'
                    }}
                  >
                    <div style={{ fontWeight: 600, marginBottom: 4, color: brand.c.navy }}>
                      {thread.subject}
                    </div>
                    <div style={{ fontSize: 12, color: '#6B7280' }}>
                      {thread.messages.length} message{thread.messages.length !== 1 ? 's' : ''}
                    </div>
                  </button>
                ))}
              </div>
            </Card>
          )}
          
          {/* Message Thread */}
          <Card>
            {threads.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '60px 20px' }}>
                <div style={{
                  width: 80,
                  height: 80,
                  borderRadius: '50%',
                  background: '#F3F4F6',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 20px'
                }}>
                  <Icon name="msgCircle" size={40} color="#9CA3AF" />
                </div>
                <h3 style={{ fontSize: 20, fontWeight: 700, marginBottom: 12 }}>
                  No Messages Yet
                </h3>
                <p style={{ color: '#6B7280', marginBottom: 24, maxWidth: 400, margin: '0 auto 24px' }}>
                  Start a conversation with our pharmacy team
                </p>
              </div>
            ) : (
              <>
                <div style={{
                  borderBottom: '1px solid #E5E7EB',
                  paddingBottom: 16,
                  marginBottom: 24
                }}>
                  <h2 style={{ fontSize: 20, fontWeight: 700, color: brand.c.navy }}>
                    {selectedThread?.subject}
                  </h2>
                </div>
                
                {/* Messages */}
                <div style={{
                  maxHeight: 400,
                  overflowY: 'auto',
                  marginBottom: 24
                }}>
                  {selectedThread?.messages.map(msg => (
                    <div
                      key={msg.id}
                      style={{
                        display: 'flex',
                        justifyContent: msg.sender === 'patient' ? 'flex-end' : 'flex-start',
                        marginBottom: 16
                      }}
                    >
                      <div style={{
                        maxWidth: '70%',
                        padding: 12,
                        borderRadius: 12,
                        background: msg.sender === 'patient' ? brand.c.accent : '#F3F4F6',
                        color: msg.sender === 'patient' ? 'white' : '#374151'
                      }}>
                        <div style={{ fontSize: 14, lineHeight: 1.5, marginBottom: 4 }}>
                          {msg.content}
                        </div>
                        <div style={{
                          fontSize: 11,
                          opacity: 0.7,
                          textAlign: 'right'
                        }}>
                          {new Date(msg.timestamp).toLocaleTimeString()}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
            
            {/* Input */}
            <div style={{ display: 'flex', gap: 12 }}>
              <input
                type="text"
                value={newMessage}
                onChange={e => setNewMessage(e.target.value)}
                onKeyPress={e => e.key === 'Enter' && handleSendMessage()}
                placeholder="Type your message..."
                style={{
                  flex: 1,
                  padding: '12px 16px',
                  border: '2px solid #E5E7EB',
                  borderRadius: 8,
                  fontSize: 14,
                  fontFamily: 'Libre Franklin, sans-serif'
                }}
              />
              <Button
                icon="send"
                onClick={handleSendMessage}
                disabled={!newMessage.trim()}
              >
                Send
              </Button>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}